import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  ScrollText,
  Scale,
  FileBarChart2,
  Briefcase,
  Search,
  Calculator,
} from "lucide-react";
import { FIRM, SERVICES, INDUSTRIES, STATS, VALUES, INSIGHTS } from "../lib/constants";
import { Button } from "../components/ui/button";

const ICONS = {
  "external-audit": ShieldCheck,
  "internal-audit": ScrollText,
  "tax-advisory": Calculator,
  "ifrs-advisory": FileBarChart2,
  "risk-compliance": Scale,
  "forensic-audit": Search,
  bookkeeping: Briefcase,
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1763454242946-948f870d730c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2dhZGlzaHUlMjBza3lsaW5lfGVufDB8fHx8MTc3ODkyMDU3N3ww&ixlib=rb-4.1.0&q=85)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden="true" />
        <div className="absolute inset-0 paper-grain opacity-60" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-28 md:pt-40 md:pb-44">
          <div className="max-w-3xl">
            <div className="overline anim-fade-in">
              <span className="rule-teal">Mogadishu · Somalia</span>
            </div>
            <h1 className="font-display mt-6 text-[2.65rem] sm:text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight text-[#0B2E5C] anim-fade-up">
              The balance between numbers <span className="italic text-[#2BA8B8]">and</span> trust.
            </h1>
            <p className="mt-6 sm:mt-8 max-w-xl text-base sm:text-lg text-[#475569] leading-relaxed font-light anim-fade-up delay-1">
              Mizan Audit & Assurance LLP is an independent audit practice serving
              banks, NGOs, corporates and the public sector across Somalia and the
              Horn of Africa — to International Standards on Auditing.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 anim-fade-up delay-2">
              <Link to="/contact" data-testid="hero-cta-proposal">
                <Button className="rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-12 px-7 text-sm tracking-wide">
                  Request a Proposal
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-cta-services">
                <Button
                  variant="outline"
                  className="rounded-sm border-[#0B2E5C] text-[#0B2E5C] hover:bg-[#0B2E5C] hover:text-white h-12 px-7 text-sm tracking-wide"
                >
                  Explore Services
                </Button>
              </Link>
            </div>

            <div className="mt-14 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-6 sm:gap-x-10 gap-y-6 anim-fade-up delay-3">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-display text-3xl sm:text-4xl text-[#0B2E5C]">{s.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#475569]">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="home-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline rule-teal">Who we are</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-4 leading-tight text-[#0B2E5C]">
              An audit practice built for accountability.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[#475569] leading-relaxed font-light text-lg">
            <p>
              <span className="font-display text-[#0B2E5C] italic">Mizan</span>{" "}
              — Arabic for <em>the balance</em> — is the principle that guides
              every engagement. Independence, technical rigor and a clear point
              of view delivered without compromise.
            </p>
            <p>
              Our partners and managers have trained at international firms and
              now bring that discipline to clients in Somalia: banks, MFIs,
              telecoms, NGOs, donor‑funded programs and the public sector.
            </p>
            <Link
              to="/about"
              data-testid="intro-about-link"
              className="link-underline inline-flex items-center text-[#0B2E5C] font-medium pt-2"
            >
              More about the firm <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-[#F8FAFC]" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline rule-teal">What we do</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C] max-w-2xl">
                Seven disciplines. One standard of work.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all-link"
              className="link-underline inline-flex items-center text-[#0B2E5C] font-medium"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.slug];
              const isWide = i === 0;
              return (
                <Link
                  to="/services"
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className={`group bg-white p-8 md:p-10 card-lift relative ${
                    isWide ? "md:col-span-2 lg:col-span-2" : ""
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <Icon
                      className="h-7 w-7 text-[#2BA8B8]"
                      strokeWidth={1.4}
                    />
                    <ArrowRight className="h-4 w-4 text-[#475569] group-hover:text-[#0B2E5C] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display text-2xl mt-8 text-[#0B2E5C] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[#475569] font-light leading-relaxed">
                    {s.short}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALUES + IMAGE */}
      <section className="bg-white border-y border-[#E2E8F0]" data-testid="home-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline rule-teal">The way we work</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C]">
              Four principles, applied to every file.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
              {VALUES.map((v) => (
                <div key={v.title} data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="text-[#2BA8B8] font-semibold text-sm tracking-wide">
                    — {v.title}
                  </div>
                  <p className="mt-3 text-[#475569] font-light leading-relaxed">
                    {v.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <img
              src="https://images.unsplash.com/photo-1739298061707-cefee19941b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3ODkyMDU3N3ww&ixlib=rb-4.1.0&q=85"
              alt="Audit team in meeting"
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#0B2E5C] text-white p-8 max-w-xs">
              <div className="font-display text-2xl leading-tight">
                "We don't sign opinions we cannot defend."
              </div>
              <div className="mt-4 text-xs uppercase tracking-[0.18em] text-[#2BA8B8]">
                — Engagement Partner
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-[#0B2E5C] text-white" data-testid="home-industries">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <div className="overline !text-[#2BA8B8]">
                <span className="rule-teal">Industries</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">
                Sector depth across the Somali economy.
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-white/70 font-light leading-relaxed text-lg self-end">
              From systemically important banks to grassroots NGOs, our teams bring
              relevant industry experience to every engagement.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {INDUSTRIES.map((ind) => (
              <div
                key={ind.name}
                data-testid={`industry-tile-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-[#0B2E5C] hover:bg-[#082142] transition-colors p-8 min-h-[180px] flex flex-col justify-between border border-white/0 hover:border-[#2BA8B8]/50"
              >
                <h3 className="font-display text-xl leading-snug">{ind.name}</h3>
                <p className="text-sm text-white/60 font-light mt-3">{ind.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/industries"
              data-testid="industries-explore-link"
              className="link-underline inline-flex items-center text-white font-medium"
            >
              Explore industries <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="bg-white" data-testid="home-insights">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline rule-teal">Insights</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C]">
                Notes from the engagement room.
              </h2>
            </div>
            <Link to="/insights" data-testid="insights-view-all-link" className="link-underline text-[#0B2E5C] font-medium">
              All articles <ArrowRight className="h-4 w-4 inline" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {INSIGHTS.slice(0, 3).map((article) => (
              <article
                key={article.slug}
                data-testid={`insight-card-${article.slug}`}
                className="bg-white p-8 card-lift"
              >
                <div className="overline">{article.category}</div>
                <h3 className="font-display text-2xl text-[#0B2E5C] mt-4 leading-tight">
                  {article.title}
                </h3>
                <p className="mt-4 text-[#475569] font-light leading-relaxed text-sm">
                  {article.excerpt}
                </p>
                <div className="mt-6 flex items-center justify-between text-xs text-[#475569]">
                  <span>{article.date}</span>
                  <span>{article.readTime}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[#F1F5F9]" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline rule-teal">Engage Mizan</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C]">
              Tell us about your audit, advisory or compliance need.
            </h2>
            <p className="mt-4 text-[#475569] font-light leading-relaxed text-lg max-w-xl">
              A short conversation is usually enough for us to scope an engagement.
              We respond within one business day.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/contact" data-testid="cta-strip-contact-link">
              <Button className="w-full rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-14 text-sm tracking-wide">
                Start the conversation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href={FIRM.phoneHref} data-testid="cta-strip-call-link">
              <Button
                variant="outline"
                className="w-full rounded-sm border-[#0B2E5C] text-[#0B2E5C] hover:bg-[#0B2E5C] hover:text-white h-14 text-sm tracking-wide"
              >
                Or call {FIRM.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
